import { useCallback } from 'react';
import {
  ToolCategory,
  AI_WRITER_TOOL,
  ICON_COLOR,
  OTHER_TOOLS_AI_WRITER,
} from '@quicktools/shared-components/data';
import { Tag } from '@quicktools/shared-components/components/core/Tag';
import WithLanding from '@quicktools/shared-components/components/landingPages/WithLanding2';
import TagLines from '@quicktools/shared-components/components/landingPages/TagLines';
import ToolRating from '@quicktools/shared-components/components/landingPages/ToolRating';
import { ImageWithTextList } from '@quicktools/shared-components/components/landingPages/ImageWithTextList';
import { IMG_WITH_TEXT_SIZES } from '@quicktools/shared-components/components/landingPages/constants';
import TabSection from '@quicktools/shared-components/components/landingPages/TabSection2';
import { ImageWithTextListContained } from '@quicktools/shared-components/components/landingPages/ImageWithTextListContained';
import MinimalFaq from '@quicktools/shared-components/components/landingPages/MinimalFaq';
import { OtherToolsByCategory } from '@quicktools/shared-components/components/landingPages/OtherToolsByCategory';
import { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import analytics from '../../../../services/analytics';
import CopywriterToolsSection from '../CopywriterToolsSection';
import GenerateExample from '../../GenerateExample';

import useStyles from './styles';

const BREADCRUMBS = [
  {
    text: 'Home',
    href: '/',
  },
  {
    text: 'Text',
    href: '/text/',
  },
  {
    text: 'AI Writer',
    href: '',
  },
];

function Home() {
  const { t } = useTranslation();
  const classes = useStyles();

  const taglinesItems = [0, 1, 2, 3].map((i) => ({
    text: <Trans i18nKey={`generalLanding.tagLines.${i}`} components={{ br: <br /> }} />,
  }));

  const imgsWithTextItems = [0, 1].map((i) => ({
    src: t(`generalLanding.imageWithText.${i}.src`),
    title: <Trans i18nKey={`generalLanding.imageWithText.${i}.title`} components={{ b: <b /> }} />,
    description: (
      <Trans
        i18nKey={`generalLanding.imageWithText.${i}.description`}
        components={{ span: <span /> }}
      />
    ),
    sizes: IMG_WITH_TEXT_SIZES,
    srcSet: `${t(`generalLanding.imageWithText.${i}.mobileSrc`)} 327w, ${t(
      `generalLanding.imageWithText.${i}.src`,
    )} 648w`,
  }));

  const tabSectionItems = [0, 1, 2, 3, 4].map((i) => ({
    title: t(`generalLanding.tabSection.items.${i}.title`),
    description: t(`generalLanding.tabSection.items.${i}.description`),
    src: t(`generalLanding.tabSection.items.${i}.src`),
    mobileSrc: t(`generalLanding.tabSection.items.${i}.mobileSrc`),
    alt: t(`generalLanding.tabSection.items.${i}.title`),
  }));

  const containedItems = [0, 1, 2, 3].map((i) => ({
    title: t(`generalLanding.containedSection.items.${i}.title`),
    description: (
      <Trans
        i18nKey={`generalLanding.containedSection.items.${i}.description`}
        components={{ a: <a /> }}
      />
    ),
    src: t(`generalLanding.containedSection.items.${i}.src`),
    alt: t(`generalLanding.containedSection.items.${i}.alt`),
  }));

  const faqItems = [0, 1, 2, 3, 4, 5].map((i) => ({
    title: t(`generalLanding.faq.${i}.title`),
    description: t(`generalLanding.faq.${i}.description`),
  }));

  const onSelectCategory = useCallback(
    (appCategory: string) => {
      analytics.toolCategorySelect({
        action_type: `${appCategory.toLocaleLowerCase()}_tools`,
        page_name: 'ai_writer_pillar',
        category: window.location.pathname.split('/')[1] + '_tools',
      });
    },
    [analytics],
  );

  useEffect(() => {
    analytics.pageOpen(window.location.pathname);
  }, []);

  const mainArea = (
    <div>
      <div className={classes.container}>
        <div className={classes.ratingContainer}>
          <Tag color={ICON_COLOR.lightGreen} className={classes.tag}>
            {t('generalLanding.tag')}
          </Tag>
          <ToolRating title={null} review={4.7} reviewsNumber={868} className={classes.rating} />
        </div>
        <h1 className={classes.heading}>
          <Trans i18nKey="generalLanding.heading" components={{ b: <b /> }}></Trans>
        </h1>
        <p className={classes.description}>{t('generalLanding.description')}</p>
        <GenerateExample />
      </div>
      <CopywriterToolsSection />
      <div className={classes.container}>
        <TagLines items={taglinesItems} listClassName={classes.listClassName} />
      </div>

      <ImageWithTextList inverse items={imgsWithTextItems} />
      <TabSection
        title={<Trans i18nKey="generalLanding.tabSection.title" components={{ b: <b /> }} />}
        items={tabSectionItems}
      />
      <ImageWithTextListContained
        title={<Trans i18nKey="generalLanding.containedSection.title" components={{ b: <b /> }} />}
        items={containedItems}
      />
      <MinimalFaq items={faqItems} />
      <OtherToolsByCategory
        className={classes.otherTools}
        items={OTHER_TOOLS_AI_WRITER}
        onCategorySelect={onSelectCategory}
      />
    </div>
  );

  return (
    <WithLanding
      hasLogin
      analytics={analytics}
      breadcrumbs={BREADCRUMBS}
      currentTool={AI_WRITER_TOOL}
      toolCategory={ToolCategory.Text}
      mainArea={mainArea}
    ></WithLanding>
  );
}
export default Home;
